import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useModalControl } from '@frontend/design-system';
import { useActiveFilters } from '../../hooks/use-active-filters';
import { useCallIntelShallowStore } from '../../hooks/use-call-intel-store';
import { FilterValues } from '../types';
import { CallIntelFiltersContext } from './call-intel-filters.context';

export interface CallIntelFiltersProviderProps {
  onApplyingFilters?: () => void;
  isLoading?: boolean;
}

export const CallIntelFiltersProvider: FC<PropsWithChildren<CallIntelFiltersProviderProps>> = ({
  children,
  onApplyingFilters,
  isLoading = false,
}) => {
  const [errors, setErrors] = useState<Partial<Record<keyof FilterValues, string | boolean>>>({});
  const [filtersModified, setFiltersModified] = useState<Partial<Record<keyof FilterValues, boolean>>>({});
  const trayControlProps = useModalControl();

  const {
    filters: storeFilters,
    allSelectedLocationIds: storeAllSelectedLocationIds,
    updateFilterValues: updateStoreFilterValues,
    resetFilters: resetStoreFilters,
  } = useActiveFilters();
  const { setFilterHintText, autoResetFilters, setAutoResetFilters } = useCallIntelShallowStore(
    'setFilterHintText',
    'autoResetFilters',
    'setAutoResetFilters'
  );

  const updateErrors = useCallback((newErrors: Partial<Record<keyof FilterValues, string | boolean>>) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      ...newErrors,
    }));
  }, []);

  const updateFilterModified = useCallback((modifiedFilters: Partial<Record<keyof FilterValues, boolean>>) => {
    setFiltersModified((prevValues) => ({
      ...prevValues,
      ...modifiedFilters,
    }));
  }, []);

  const updateFilterValues = useCallback(
    (filters: FilterValues) => {
      updateStoreFilterValues(filters);
      setAutoResetFilters({});
      onApplyingFilters?.();
    },
    [onApplyingFilters, updateStoreFilterValues, setAutoResetFilters]
  );

  const resetFilters = useCallback(() => {
    setFiltersModified({});
    setErrors({});
    setAutoResetFilters({});
    resetStoreFilters();
    onApplyingFilters?.();
  }, [resetStoreFilters, onApplyingFilters, setAutoResetFilters]);

  const contextValue = useMemo(
    () => ({
      isTrayOpen: trayControlProps.modalProps.show,
      trayControlProps,
      filterValues: storeFilters,
      allSelectedLocationIds: storeAllSelectedLocationIds,
      filtersModified,
      hasAnyFilterModified: Object.values(filtersModified).some((isModified) => isModified),
      errors,
      isLoading,
      autoResetFilters,
      updateFilterModified,
      updateFilterValues,
      updateErrors,
      setAutoResetFilters,
      resetFilters,
      setFilterHintText,
    }),
    [
      trayControlProps,
      storeFilters,
      storeAllSelectedLocationIds,
      filtersModified,
      errors,
      isLoading,
      autoResetFilters,
      updateFilterModified,
      updateFilterValues,
      updateErrors,
      setAutoResetFilters,
      resetFilters,
      setFilterHintText,
    ]
  );

  return <CallIntelFiltersContext.Provider value={contextValue}>{children}</CallIntelFiltersContext.Provider>;
};
